import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { getCurrentRouteParams } from '@/utils';
import type { Network, SelectedNetwork } from '@/types';

/**
 * Derives the selected network directly from URL params.
 * @param {Network[] | undefined} networks - The networks.
 * @param {boolean} isPending - Whether the networks are pending.
 * @returns {DerivedNetworkResult} The derived network result including shows.
 */
const useDerivedNetwork = (
  networks: Network[] | undefined,
  isPending: boolean
): SelectedNetwork => {
  const params = useParams();
  const { networkId } = getCurrentRouteParams(params);

  return useMemo(() => {
    if (isPending || !networks || !networkId || networks.length === 0) {
      return {
        id: undefined,
        name: undefined,
        shows: [],
      };
    }

    // Find network details from ID in URL
    const network = networks.find((n) => n.id === networkId);

    return {
      id: network?.id,
      name: network?.name ?? undefined,
      shows: network?.shows ?? [],
    };
  }, [isPending, networks, networkId]);
};

export default useDerivedNetwork;
